import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import EntityList from "src/components/hallinta/EntityList";
import EntityFormDialog from "src/components/hallinta/EntityFormDialog";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import * as api from "src/utils/hallinta/hallintaApi";
import { canMakePersonGlobal, isPersonGlobal } from "src/utils/hallinta/personGlobal";
import { captureError } from "src/utils/errorReporting";

// All person fields are individually optional, but a person can't be entirely
// blank — at least one field must be filled (enforced via validateForm below).
// personMemo is the free-text comment/note (same column puminet4's EditPerson
// edits as "Kommentti..."; the list proc returns it so edits don't wipe it).
// Labels are language-bearing, so this (and PERSON_SORT_OPTIONS/ROLES below)
// is a function of `t` rather than a module-level constant — a plain constant
// would freeze the labels to Finnish at import (see quoteConstants.js's
// *Options(t) precedent).
function basePersonSchema(t) {
  return [
    { key: "personFirstName", label: t("provider.people.firstNameLabel"), type: "text" },
    { key: "personLastName", label: t("provider.people.lastNameLabel"), type: "text" },
    { key: "personEmail", label: t("provider.people.emailLabel"), type: "email" },
    { key: "personPhone", label: t("provider.people.phoneLabel"), type: "text" },
    { key: "personMemo", label: t("provider.people.memoLabel"), type: "text", multiline: true },
  ];
}

// Sort the person list by first name → last name → email → phone (Finnish
// locale, case-insensitive); empty fields sort first.
const PERSON_SORT_KEYS = ["personFirstName", "personLastName", "personEmail", "personPhone"];
// Selectable sort fields for the shared EntityList controls (PERSON_SORT_KEYS
// doubles as the free-text searchKeys).
function personSortOptions(t) {
  return [
    { key: "personFirstName", label: t("provider.people.firstNameLabel") },
    { key: "personLastName", label: t("provider.people.lastNameLabel") },
    { key: "personEmail", label: t("provider.people.emailLabel") },
    { key: "personPhone", label: t("provider.people.phoneLabel") },
  ];
}
function comparePersons(a, b) {
  for (const k of PERSON_SORT_KEYS) {
    const cmp = String(a[k] ?? "").localeCompare(String(b[k] ?? ""), "fi", { sensitivity: "base" });
    if (cmp !== 0) return cmp;
  }
  return 0;
}

function personRoles(t) {
  return [
    { typeId: 2, formKey: "roleAdmin",    label: t("provider.people.roleAdminLabel"),    chip: t("provider.people.roleAdminChip") },
    { typeId: 1, formKey: "roleTarjous",  label: t("provider.people.roleTarjousLabel"),  chip: t("provider.people.roleTarjousChip") },
    { typeId: 8, formKey: "rolePumppari", label: t("provider.people.rolePumppariLabel"), chip: t("provider.people.rolePumppariLabel") },
  ];
}

export default function PeopleTab({ company, canEdit, isAdmin }) {
  const { token, user } = useAuth();
  const t = useT();
  const tenant = company.ownerAsiakasId ?? company.asiakasId;
  const ROLES = useMemo(() => personRoles(t), [t]);
  const BASE_PERSON_SCHEMA = useMemo(() => basePersonSchema(t), [t]);
  const [data, setData] = useState({ personList: null, settings: [] });
  const [error, setError] = useState(false);
  const [editRow, setEditRow] = useState(undefined); // undefined=closed, null=create, row=edit
  const [globalConfirm, setGlobalConfirm] = useState(null); // row pending make-global
  const [globalBusy, setGlobalBusy] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const res = await api.listPersons({ token, asiakasId: company.asiakasId });
      setData({ personList: res?.personList || [], settings: res?.asiakasPersonSettings || [] });
    } catch (err) {
      captureError(err, { tags: { entity: "person", operation: "list" } });
      setError(true); setData({ personList: [], settings: [] });
    }
  }, [token, company.asiakasId]);

  useEffect(() => { (async () => { await load(); })(); }, [load]);

  const rolesByPerson = useMemo(() => {
    const m = new Map();
    for (const s of data.settings) {
      const arr = m.get(s.personId) || [];
      arr.push(s); m.set(s.personId, arr);
    }
    return m;
  }, [data.settings]);

  const hasRole = (personId, typeId) =>
    (rolesByPerson.get(personId) || []).some((s) => Number(s.asiakasPersonSettingTypeId) === typeId);

  const schema = useMemo(
    () => [...BASE_PERSON_SCHEMA, ...(isAdmin ? ROLES.map((r) => ({ key: r.formKey, label: r.label, type: "switch" })) : [])],
    [isAdmin, BASE_PERSON_SCHEMA, ROLES]
  );

  async function syncRole(personId, typeId, want) {
    const existing = (rolesByPerson.get(personId) || []).find(
      (s) => Number(s.asiakasPersonSettingTypeId) === typeId
    );
    if (want && !existing) await api.addRole({ token, asiakasId: company.asiakasId, personId, typeId });
    else if (!want && existing) await api.removeRole({ token, asiakasPersonSettingId: existing.asiakasPersonSettingId });
  }

  async function submitPerson(form) {
    if (editRow) {
      const { __key: _k, ...all } = editRow;
      const rest = Object.fromEntries(Object.entries(all).filter(([k]) => !ROLES.some((r) => r.formKey === k)));
      await api.savePerson({
        token,
        fields: {
          ...rest,
          personId: editRow.personId,
          personFirstName: form.personFirstName,
          personLastName: form.personLastName,
          personEmail: form.personEmail,
          personPhone: form.personPhone,
          personMemo: form.personMemo,
        },
      });
      if (isAdmin) {
        for (const r of ROLES) await syncRole(editRow.personId, r.typeId, form[r.formKey]);
      }
    } else {
      const personId = await api.createPerson({ token, ownerAsiakasId: tenant, fields: form });
      if (personId) await api.addRole({ token, asiakasId: company.asiakasId, personId, typeId: 8 });
    }
    await load();
  }

  async function doMakeGlobal() {
    if (!globalConfirm) return;
    setGlobalBusy(true);
    try {
      // Not UI copy — an internal changeTracker/X-Action-Reason audit annotation
      // (see @ibetoni/constants change-tracker convention), not rendered to any
      // user in this file's scope. Left untranslated deliberately.
      await api.setPersonOwner({ token, personId: globalConfirm.personId, ownerAsiakasId: null, reason: "Tee globaaliksi (Hallinta)" });
      setGlobalConfirm(null);
      setEditRow(undefined); // close the edit modal the action was launched from
      await load();
    } catch (err) {
      captureError(err, { tags: { entity: "person", operation: "make-global" } });
    } finally {
      setGlobalBusy(false);
    }
  }

  const rows = (data.personList || []).map((p) => ({ ...p, __key: p.personId })).sort(comparePersons);

  return (
    <Box>
      <EntityList
        title={t("provider.people.heading")}
        rows={rows}
        loading={data.personList === null}
        error={error}
        canEdit={canEdit}
        entityTag="person"
        searchKeys={PERSON_SORT_KEYS}
        sortOptions={personSortOptions(t)}
        onAdd={() => setEditRow(null)}
        onEdit={(row) => setEditRow({
          ...row,
          ...Object.fromEntries(ROLES.map((r) => [r.formKey, hasRole(row.personId, r.typeId)])),
        })}
        onDelete={async (row) => {
          await api.removePersonFromCompany({ token, asiakasId: company.asiakasId, personId: row.personId });
          await load();
        }}
        renderRow={(p) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.personFirstName} {p.personLastName}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>{p.personEmail} · {p.personPhone}</Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }}>
              {(rolesByPerson.get(p.personId) || [])
                .map((s) => ROLES.find((r) => r.typeId === Number(s.asiakasPersonSettingTypeId))?.chip)
                .filter(Boolean)
                .map((l, i) => <Chip key={"r" + i} size="small" label={l} />)}
            </Stack>
          </Box>
        )}
      />
      {editRow !== undefined && (
        <EntityFormDialog
          open
          onClose={() => setEditRow(undefined)}
          title={editRow ? t("provider.people.editDialogTitle") : t("provider.people.addDialogTitle")}
          schema={schema}
          row={editRow}
          entityTag="person"
          validateForm={(form) =>
            BASE_PERSON_SCHEMA.some((f) => String(form[f.key] ?? "").trim())
              ? null
              : t("provider.people.validateOneFieldRequired")
          }
          onSubmit={submitPerson}
        >
          {editRow && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("provider.people.globalVisibilityLabel")}</Typography>
              {isPersonGlobal(editRow)
                ? <Chip size="small" color="info" label={t("provider.people.globalChip")} />
                : <Chip size="small" variant="outlined" label={t("provider.people.companySpecificChip")} />}
              {canMakePersonGlobal(user, editRow) && (
                <Button size="small" variant="outlined" color="info" startIcon={<PublicIcon />}
                  onClick={() => setGlobalConfirm(editRow)}>
                  {t("provider.people.makeGlobalCta")}
                </Button>
              )}
            </Box>
          )}
        </EntityFormDialog>
      )}

      <Dialog open={!!globalConfirm} onClose={globalBusy ? undefined : () => setGlobalConfirm(null)}>
        <DialogTitle>{t("provider.people.makeGlobalDialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("provider.people.makeGlobalDialogBody")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGlobalConfirm(null)} disabled={globalBusy}>{t("provider.shared.cancel")}</Button>
          <Button color="info" onClick={doMakeGlobal} disabled={globalBusy}
            startIcon={globalBusy ? <CircularProgress size={16} color="inherit" /> : null}>
            {t("provider.people.makeGlobalCta")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
